import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronRight, Trophy, Users } from 'lucide-react';
import api from '../../api/client';
import useIsMobile from '../../utils/useIsMobile';

// Statistiche società FIPAV (29/10/2026, titolare: "vedere chi nell'anno si è interessato al progetto FIPAV",
// adesione ed effettiva partecipazione: iscritti e non entrati, entrati, podio). Solo Campionato per società,
// per stagione sportiva. Chi ha giocato e il piazzamento vengono dalla classifica finale FIPAV di ogni tappa.
// Per ora solo nella vista società; più avanti si potrà condividere con gli atleti.
export const ESITO = {
  GIOCATA:       { l: 'Ha giocato',                     col: '#15803D', bg: '#DCFCE7' },
  NON_ENTRATA:   { l: 'Non entrata',                    col: '#B42318', bg: '#FEE4E2', t: 'Riserva o fuori dalla lista d\'ingresso FIPAV' },
  ASSENTE:       { l: 'In tabellone, non in classifica', col: '#B45309', bg: '#FEF3C7', t: 'Era nella lista d\'ingresso ma non compare nella classifica finale: ritiro o assenza?' },
  IN_ATTESA:     { l: 'Risultati in arrivo',            col: '#6E6E73', bg: '#F2F2F5', t: 'La classifica finale FIPAV non è ancora pubblicata' },
  DA_VERIFICARE: { l: 'Torneo sparito dal calendario',  col: '#B45309', bg: '#FEF3C7', t: 'Il torneo non è più nel calendario FIPAV e non c\'è classifica: forse annullato, verificalo e confermalo dalla scheda del torneo' },
  IN_PROGRAMMA:  { l: 'In programma',                   col: '#1D4ED8', bg: '#DBEAFE' },
  NON_ISCRITTA:  { l: 'Non iscritta su BVL',            col: '#6E6E73', bg: '#F2F2F5', t: 'La richiesta è rimasta in attesa: la società non l\'ha iscritta' },
  RITIRATA:      { l: 'Ritirata',                       col: '#6E6E73', bg: '#F2F2F5' },
  RIFIUTATA:     { l: 'Rifiutata',                      col: '#B42318', bg: '#FEE4E2' },
  ANNULLATO:     { l: 'Torneo annullato',               col: '#7A271A', bg: '#FEE4E2' },
};
const gg = (iso) => iso.split('-').reverse().join('/');
const nomeTorneo = (s) => s.replace(/campionato italiano per società\s*/i, '').replace(/ · \d{2}\/\d{2}\/\d{4}/, '');

function EsitoBadge({ x }) {
  const e = ESITO[x.esito] || { l: x.esito, col: '#6E6E73', bg: '#F2F2F5' };
  const podio = x.esito === 'GIOCATA' && x.posizione && x.posizione <= 3;
  const testo = x.esito === 'GIOCATA' && x.posizione ? `${podio ? 'Podio · ' : ''}${x.posizione}°${x.punti != null ? ` · ${String(x.punti).replace('.', ',')} pt` : ''}` : e.l;
  return <span title={e.t} style={{ ...s.badge, color: podio ? '#92400E' : e.col, background: podio ? '#FDE68A' : e.bg }}>{podio && <Trophy size={12} />}{testo}</span>;
}

export default function StatisticheFipav({ onApri }) {
  const mobile = useIsMobile();
  const [stagione, setStagione] = useState('');
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [filtro, setFiltro] = useState(''); // '' | giocato | podio | non_entrati | mai
  const [aperto, setAperto] = useState(null);
  useEffect(() => {
    setD(null); setErr('');
    api.get('/fipav/statistiche', { params: { stagione: stagione || undefined } }).then(r => setD(r.data)).catch(e => setErr(e?.response?.data?.error || 'Impossibile caricare le statistiche'));
  }, [stagione]);

  const atleti = useMemo(() => {
    const parole = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (d?.atleti || []).filter(a => parole.every(p => a.nome.toLowerCase().includes(p))).filter(a =>
      !filtro ? true : filtro === 'giocato' ? a.conteggi.GIOCATA > 0 : filtro === 'podio' ? a.podi > 0
        : filtro === 'non_entrati' ? a.conteggi.NON_ENTRATA > 0 : a.conteggi.GIOCATA === 0);
  }, [d, q, filtro]);

  if (err) return <div style={s.err}>{err}</div>;
  if (!d) return <div style={s.vuoto}>Caricamento… (la prima volta si scaricano le classifiche finali FIPAV)</div>;
  const r = d.riepilogo;
  const kpi = [
    ['Atleti coinvolti', r.atleti, 'Hanno chiesto almeno un torneo o hanno giocato per B-Cheers'],
    ['Iscrizioni', r.iscrizioni, 'Richieste di coppia nel gestionale'],
    ['Atleti che hanno giocato', r.atleti_giocato, 'Presenti in almeno una classifica finale'],
    ['Coppie in campo', r.coppie_giocate, 'Coppie B-Cheers nelle classifiche finali'],
    ['Podi', r.podi, '1°, 2° o 3° posto'],
    ['Coppie non entrate', r.non_entrate, 'Riserve o fuori lista'],
    ['In programma', r.in_programma, 'Iscrizioni a tornei non ancora giocati'],
    ['Annullate', r.annullate, 'Tornei annullati dalla FIPAV'],
  ];
  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '14px' }}>
        <select style={s.pill} value={d.stagione} onChange={e => setStagione(e.target.value)}>
          {d.stagioni.map(x => <option key={x} value={x}>Stagione {x}</option>)}
        </select>
        <span style={s.nota}>Campionato Italiano per società · chi ha giocato e il piazzamento dalle classifiche finali FIPAV, aggiornate da sole dopo ogni tappa</span>
      </div>
      <div style={s.kpi}>
        {kpi.map(([l, v, t]) => <div key={l} style={s.tile} title={t}><div style={s.num}>{v}</div><div style={s.lab}>{l}</div></div>)}
      </div>
      {r.senza_richiesta > 0 && <div style={{ ...s.nota, margin: '8px 0 0' }}>{r.senza_richiesta} {r.senza_richiesta === 1 ? 'coppia B-Cheers ha giocato' : 'coppie B-Cheers hanno giocato'} senza passare dal gestionale (riconosciute dalla classifica FIPAV).</div>}

      <h2 style={s.h2}><Users size={18} /> Atleti</h2>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
        <div style={s.cerca}><Search size={15} color="var(--text-dim)" /><input style={s.cercaInput} placeholder="Cerca atleta…" value={q} onChange={e => setQ(e.target.value)} /></div>
        {[['', 'Tutti'], ['giocato', 'Hanno giocato'], ['podio', 'Podio'], ['non_entrati', 'Non entrati'], ['mai', 'Mai giocato']].map(([v, l]) =>
          <button key={v} style={{ ...s.pill, ...(filtro === v ? s.pillOn : {}) }} onClick={() => setFiltro(v)}>{l}</button>)}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.tab}>
          <thead><tr>
            <th style={{ ...s.th, textAlign: 'left' }}>Atleta</th>
            <th style={s.th} title="Richieste nel gestionale">Iscrizioni</th>
            <th style={s.th}>Giocati</th>
            <th style={s.th}>Podi</th>
            <th style={s.th} title="Riserva o fuori lista">Non entrati</th>
            {!mobile && <th style={s.th} title="Ritirate, rifiutate o non iscritte su BVL">Ritirate</th>}
            {!mobile && <th style={s.th}>Annullati</th>}
            {!mobile && <th style={s.th}>In programma</th>}
            <th style={s.th}>Migliore</th>
          </tr></thead>
          <tbody>
            {atleti.length === 0 && <tr><td colSpan={9} style={{ ...s.td, textAlign: 'center', color: 'var(--text-dim)' }}>Nessun atleta</td></tr>}
            {atleti.map(a => {
              const c = a.conteggi, open = aperto === a.chiave;
              return (
                <React.Fragment key={a.chiave}>
                  <tr style={{ cursor: 'pointer', ...(open ? { background: 'var(--bg-input)' } : {}) }} onClick={() => setAperto(open ? null : a.chiave)}>
                    <td style={{ ...s.td, textAlign: 'left', fontWeight: 600 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{a.nome}</span>
                      {!a.collegato && <span style={s.mini} title="Riconosciuto solo dalla classifica FIPAV: nessun account collegato">dalla classifica FIPAV</span>}
                    </td>
                    <td style={s.td}>{a.iscrizioni}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: c.GIOCATA ? '#15803D' : 'var(--text-dim)' }}>{c.GIOCATA}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: a.podi ? '#92400E' : 'var(--text-dim)' }}>{a.podi || '—'}</td>
                    <td style={{ ...s.td, color: c.NON_ENTRATA ? '#B42318' : 'var(--text-dim)' }}>{c.NON_ENTRATA || '—'}</td>
                    {!mobile && <td style={s.td}>{(c.RITIRATA + c.RIFIUTATA + c.NON_ISCRITTA) || '—'}</td>}
                    {!mobile && <td style={s.td}>{c.ANNULLATO || '—'}</td>}
                    {!mobile && <td style={s.td}>{c.IN_PROGRAMMA || '—'}</td>}
                    <td style={s.td}>{a.miglior ? `${a.miglior}°` : '—'}</td>
                  </tr>
                  {open && <tr><td colSpan={9} style={{ ...s.td, textAlign: 'left', background: 'var(--bg-input)', padding: '6px 12px 12px' }}>
                    {a.tornei.map((x, i) => (
                      <div key={i} style={s.rigaT}>
                        <button style={s.linkT} onClick={() => onApri?.(x.torneo_id)}><b>{gg(x.data)}</b> · {nomeTorneo(x.torneo)}</button>
                        <span style={s.nota}>{x.compagno ? `con ${x.compagno}` : ''}{x.senza_richiesta ? ' · senza richiesta nel gestionale' : ''}</span>
                        <EsitoBadge x={x} />
                      </div>
                    ))}
                  </td></tr>}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <h2 style={s.h2}><Trophy size={18} /> Tornei</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {d.tornei.map(t => (
          <button key={t.id} style={s.torneo} onClick={() => onApri?.(t.id)}>
            <span style={{ flex: 1, minWidth: 0 }}><b>{gg(t.data)}</b> · {nomeTorneo(t.torneo)}</span>
            <span style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ ...s.badge, color: 'var(--text)', background: 'var(--bg-input)' }}>{t.iscritte} iscritte</span>
              {t.annullato ? <span style={{ ...s.badge, color: ESITO.ANNULLATO.col, background: ESITO.ANNULLATO.bg }}>Annullato</span>
                : !t.finito ? <span style={{ ...s.badge, color: ESITO.IN_PROGRAMMA.col, background: ESITO.IN_PROGRAMMA.bg }}>In programma</span>
                : !t.classifica ? <span style={{ ...s.badge, color: ESITO.IN_ATTESA.col, background: ESITO.IN_ATTESA.bg }}>Classifica non disponibile</span>
                : <>
                  <span style={{ ...s.badge, color: ESITO.GIOCATA.col, background: ESITO.GIOCATA.bg }}>{t.giocate} in campo</span>
                  {t.podi > 0 && <span style={{ ...s.badge, color: '#92400E', background: '#FDE68A' }}><Trophy size={12} />{t.podi} {t.podi === 1 ? 'podio' : 'podi'}</span>}
                  {t.non_entrate > 0 && <span style={{ ...s.badge, color: ESITO.NON_ENTRATA.col, background: ESITO.NON_ENTRATA.bg }}>{t.non_entrate} non entrate</span>}
                </>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const s = {
  err: { color: '#B42318', fontSize: '14px' },
  vuoto: { color: 'var(--text-dim)', padding: '40px 0', textAlign: 'center' },
  nota: { fontSize: '12.5px', color: 'var(--text-muted)' },
  pill: { border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text-muted)', borderRadius: '999px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  pillOn: { background: 'var(--text)', color: 'var(--bg-card)', borderColor: 'var(--text)' },
  kpi: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' },
  tile: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '12px 14px' },
  num: { fontSize: '26px', fontWeight: 800, lineHeight: 1.1 },
  lab: { fontSize: '12.5px', color: 'var(--text-muted)', fontWeight: 600, marginTop: '2px' },
  h2: { display: 'flex', gap: '8px', alignItems: 'center', fontSize: '17px', fontWeight: 700, margin: '24px 0 10px' },
  cerca: { display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '999px', padding: '0 12px', minWidth: '220px' },
  cercaInput: { flex: 1, border: 'none', background: 'transparent', padding: '8px 0', outline: 'none', color: 'var(--text)', fontSize: '14px', boxShadow: 'none' },
  tab: { width: '100%', borderCollapse: 'collapse', background: 'var(--bg-card)', borderRadius: '14px', overflow: 'hidden', fontSize: '14px' },
  th: { padding: '9px 10px', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textAlign: 'center', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'center', verticalAlign: 'top' },
  mini: { marginLeft: '6px', fontSize: '11px', fontWeight: 700, color: '#6E6E73', background: '#F2F2F5', borderRadius: '999px', padding: '1px 7px' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 700, borderRadius: '999px', padding: '3px 10px', whiteSpace: 'nowrap' },
  rigaT: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', padding: '5px 0', borderBottom: '1px dashed var(--border)' },
  linkT: { border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--text)', textAlign: 'left', fontSize: '13.5px' },
  torneo: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', width: '100%', textAlign: 'left', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '10px 12px', cursor: 'pointer', color: 'var(--text)', fontSize: '13.5px' },
};
